/* eslint-disable radix */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import { Typo } from 'uikit/content';
import { Button } from 'uikit/buttons';

import useScreenSize from '../hooks/useScreenSize';

import styles from '../styles/catalogCarousel.module.scss';

const CatalogCarousel = ({
  children,
  contentContainerClassName = '',
  hasOnlyOneItem = false,
  layoutMaxItems = 4,
  title = '',
}) => {
  const screenSize = useScreenSize();

  const containerRef = useRef(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const getCarouselRotationCount = () => {
    const rotationCountMapping = {
      xlarge: layoutMaxItems,
      large: layoutMaxItems,
      medium: layoutMaxItems,
      small: 2,
      xsmall: 1,
    };

    return rotationCountMapping[screenSize] ?? 4;
  };

  const scrollLiveVideoCarousel = (position) => {
    const container = containerRef.current;

    if (!container) return;

    const cardElement = container.firstChild;
    const cardWidth = cardElement.clientWidth;
    const rotationCount = getCarouselRotationCount() ?? 4;

    const calculation =
      position === 'left'
        ? container.scrollLeft - cardWidth * rotationCount
        : container.scrollLeft + cardWidth * rotationCount;

    container.scrollLeft = calculation;
  };

  const shouldDisplayScrollButtons = () => {
    const rotationCount = getCarouselRotationCount();

    if (children.length <= rotationCount) return false;

    return true;
  };

  const updateScrollButtonState = () => {
    const container = containerRef.current;

    if (!container) return;

    setIsLeftDisabled(container.scrollLeft === 0);
    setIsRightDisabled(container.scrollLeft + container.clientWidth >= container.scrollWidth - 10);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateScrollButtonState();
    container.addEventListener('scroll', updateScrollButtonState);

    return () => {
      container.removeEventListener('scroll', updateScrollButtonState);
    };
  }, [children]);

  const renderScrollButton = (position, className = '') => {
    const size =
      screenSize === 'small' || screenSize === 'medium' || screenSize === 'xsmall' ? 36 : 40;

    return (
      <Button
        className={className}
        data-object-name={`Carousel ${position} Arrow Button`}
        icon={
          position === 'left' ? (
            <KeyboardArrowLeft sx={{ height: size, width: size }} />
          ) : (
            <KeyboardArrowRight sx={{ height: size, width: size }} />
          )
        }
        aria-label={i18n.t(`carousel-${position}-arrow-button`)}
        onClick={() => scrollLiveVideoCarousel(position)}
        variant="ghost"
        isDisabled={position === 'left' ? isLeftDisabled : isRightDisabled}
      />
    );
  };

  return (
    <>
      <div className={styles.header}>
        {title && (
          <Typo className={styles.title} element="h4" variant="d02" modifier="bold">
            {title}
          </Typo>
        )}
        {shouldDisplayScrollButtons() && (
          <div className={styles.header_actions}>
            {renderScrollButton('left')}
            {renderScrollButton('right')}
          </div>
        )}
      </div>
      <div className={classnames(styles.content_container, contentContainerClassName)}>
        <div className={styles.content_container_inner}>
          <div className={styles.content_actions}>
            <div className={styles.content_actions_left}>
              {shouldDisplayScrollButtons() && renderScrollButton('left')}
            </div>
          </div>
          <ul
            className={classnames(styles.scroller, {
              [styles.has_only_one_item]: hasOnlyOneItem,
            })}
            id="scroll-container"
            ref={containerRef}
          >
            {children}
          </ul>
          <div className={styles.content_actions}>
            <div className={styles.content_actions_right}>
              {shouldDisplayScrollButtons() && renderScrollButton('right')}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CatalogCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  contentContainerClassName: PropTypes.string,
  hasOnlyOneItem: PropTypes.bool,
  layoutMaxItems: PropTypes.number,
  title: PropTypes.string,
};

export default React.memo(CatalogCarousel);
